import React, { useRef, useState } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import { useNavigate } from 'react-router-dom';
import ResponsiveContainer from './ResponsiveContainer';

function DigitalSignaturePage({ signatureDataURL, setSignatureDataURL, t }) {
  const sigCanvasRef = useRef(null);
  const navigate = useNavigate();
  const [showSignaturePopup, setShowSignaturePopup] = useState(false);
  const [tempSignature, setTempSignature] = useState(null);

  const openSignaturePopup = () => {
    setShowSignaturePopup(true);
  };

  const clearSignature = () => {
    if (sigCanvasRef.current) {
      sigCanvasRef.current.clear();
      setTempSignature(null);
    }
  };

  const saveSignature = () => {
    if (sigCanvasRef.current && !sigCanvasRef.current.isEmpty()) {
      const dataURL = sigCanvasRef.current.toDataURL('image/png');
      setTempSignature(dataURL);
      setSignatureDataURL(dataURL);
      setShowSignaturePopup(false);
    } else {
      alert(t('pleaseSignBeforeSaving'));
    }
  };

  const handleBack = () => {
    navigate('/terms-conditions');
  };

  const handleNext = () => {
    if (!signatureDataURL && !tempSignature) {
      alert(t('pleaseSignFirst'));
      return;
    }
    
    // If we have a temp signature but haven't saved it to the parent state yet
    if (tempSignature && tempSignature !== signatureDataURL) {
      setSignatureDataURL(tempSignature);
    }
    
    navigate('/email-confirmation');
  };

  return (
    <ResponsiveContainer>
      <div className="min-h-[calc(100vh-100px)] bg-gray-100 py-6 px-4">
        <div className="bg-white rounded-lg shadow-lg p-6 max-w-3xl mx-auto">
          <h2 className="text-2xl font-semibold mb-6">{t('digitalSignature')}</h2>
          
          <p className="text-gray-600 mb-6">
            {t('pleaseSign')}
          </p>
          
          {/* Signature display area */}
          <div className="mb-6">
            {tempSignature || signatureDataURL ? (
              <div className="border border-gray-300 rounded-md p-4 bg-gray-50 flex justify-center">
                <img 
                  src={tempSignature || signatureDataURL} 
                  alt="Signature" 
                  className="max-h-32"
                />
              </div>
            ) : (
              <div className="border border-gray-300 rounded-md p-8 bg-gray-50 flex justify-center items-center h-32">
              </div>
            )}
          </div>
          
          {/* Signature button */}
          <div className="flex justify-center mb-8">
            <button
              onClick={openSignaturePopup}
              className="px-6 py-3 bg-[#d0a949] text-white rounded-md hover:bg-[#a6873a] flex items-center"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
              </svg>
              {tempSignature || signatureDataURL ? t('changeSignature') : t('addSignature')}
            </button>
          </div>
          
          {/* Navigation buttons */}
          <div className="flex justify-between pt-4">
            <button
              onClick={handleBack}
              className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
            >
              {t('back')}
            </button>
            
            <button
              onClick={handleNext}
              className="px-4 py-2 bg-[#d0a949] text-white rounded-md hover:bg-[#a6873a]"
            >
              {t('doneButton')}
            </button>
          </div>
        </div>
      </div>
      
      {/* Signature popup */}
      {showSignaturePopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full">
            <h3 className="text-xl font-semibold mb-4">{t('digitalSignature')}</h3>
            
            <div className="border border-gray-300 rounded-md bg-white mb-4">
              <SignatureCanvas
                ref={sigCanvasRef}
                penColor="black"
                canvasProps={{ 
                  width: 500, 
                  height: 200, 
                  className: 'signature-canvas w-full' 
                }}
                backgroundColor="white"
              />
            </div>
            
            <div className="flex justify-between">
              <button
                onClick={clearSignature}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300"
              >
                {t('clear')}
              </button>
              
              <div>
                <button
                  onClick={() => setShowSignaturePopup(false)}
                  className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 mr-2"
                >
                  {t('cancel')}
                </button>
                
                <button
                  onClick={saveSignature}
                  className="px-4 py-2 bg-[#d0a949] text-white rounded-md hover:bg-[#a6873a]"
                >
                  {t('doneButton')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </ResponsiveContainer>
  );
}

export default DigitalSignaturePage;